<template>
  <v-card
    class="food-card elevation-2"
    :class="{ 'mobile-card': device.isMobile, 'disabled-card': isDisabledCard }"
    rounded="lg"
  >
    <div v-if="isDisabledCard" class="food-card-disabled"></div>

    <v-row no-gutters class="align-center">
      <!-- Image Section with Heart Icon -->
      <v-col cols="auto" class="image-wrapper">
        <div class="image-container" @click="openPreview">
          <v-img
            v-if="meal.name !== 'Укупно'"
            :src="mealImage"
            :height="device.isMobile ? '100px' : '140px'"
            :width="device.isMobile ? '100px' : '140px'"
            cover
            class="rounded-lg"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="grey lighten-2"
                ></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </div>
      </v-col>

      <!-- Content Section -->
      <v-col class="content-section">
        <div class="d-flex flex-column fill-height justify-space-between pa-3">
          <!-- Top Section: Title and Info -->
          <div>
            <!-- Meal Name -->
            <h3
              class="meal-title mb-2"
              :class="device.isMobile ? 'text-subtitle-1' : 'text-h6'"
            >
              {{ meal.name }}
            </h3>

            <!-- Labels Section -->
            <div
              class="labels-container mb-2"
              v-if="meal.labels && meal.labels.length > 0"
            >
              <v-chip
                v-for="label in meal.labels"
                :key="label.id"
                x-small
                class="mr-1 mb-1"
                :color="getLabelColor(label)"
                :text-color="getLabelTextColor(label)"
                label
              >
                {{ label.name }}
              </v-chip>
            </div>

            <!-- Price with Icon -->
            <div v-if="showPrice && meal.price" class="price-section mb-2">
              <v-icon small color="primary darken-1" class="mr-1">mdi-tag</v-icon>
              <span class="price-text">{{ meal.price }} KM</span>
            </div>
          </div>

          <!-- Bottom Section: Quantity Controls and Action Button -->
          <div class="actions-section">
            <v-row no-gutters align="center" justify="space-between">
              <!-- Quantity Controls -->
              <v-col cols="auto">
                <div class="quantity-controls">
                  <v-btn
                    v-if="!isDisabledCard"
                    icon
                    :small="device.isMobile"
                    :color="
                      isDisabledCard || quantity <= 0 ? 'grey lighten-2' : 'primary'
                    "
                    :disabled="isDisabledCard || quantity <= 0"
                    @click="updateQuantity(-1)"
                    class="quantity-btn"
                  >
                    <v-icon :small="device.isMobile">mdi-minus</v-icon>
                  </v-btn>

                  <span class="quantity-text mx-2">{{ quantity }}</span>

                  <v-btn
                    v-if="!isDisabledCard"
                    icon
                    :small="device.isMobile"
                    :color="isDisabledCard ? 'grey lighten-2' : 'primary'"
                    :disabled="isDisabledCard"
                    @click="updateQuantity(1)"
                    class="quantity-btn"
                  >
                    <v-icon :small="device.isMobile">mdi-plus</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <!-- Action Button -->
              <v-col cols="auto">
                <primary-button
                  v-if="quantity > 0 && !isDisabledCard"
                  :small="device.isMobile"
                  :disabled="isCompanyBtnDisabled"
                  @click="onCompanyBtnClick"
                  :height="device.isMobile ? '32px' : '36px'"
                  class="action-btn"
                >
                  {{ companyBtnText }}
                </primary-button>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Desktop Dialog -->
    <company-menu-food-order-dialog
      v-model="previewDialog"
      :daily-menu="dailyMenu"
      :disabled="isDisabledCard"
      :shift="shift"
      :meal="meal"
    />
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import conf from "@/config";
import CompanyMenuFoodOrderDialog from "@/components/companyMenu/dialogs/CompanyMenuFoodOrderDialog.vue";
import CompanyMenuFoodOrderCard from "@/components/companyMenu/cards/CompanyMenuFoodOrderCard.vue";
import hasMenuOrdering from "@/mixins/hasMenuOrdering";

export default {
  name: "CompanyMenuFoodCard",
  mixins: [hasMenuOrdering],
  props: {
    meal: { type: Object, required: true },
    dailyMenu: { type: Object, required: true },
    shift: { type: String, required: true },
    disabled: { type: Boolean, default: false },
  },
  components: {
    CompanyMenuFoodOrderDialog,
    CompanyMenuFoodOrderCard,
  },
  data() {
    return {
      conf,
      previewDialog: false,
    };
  },
  computed: {
    ...mapState("companyMenu", ["dayTo"]),
    user() {
      return this.$store.state.user.user;
    },
    restaurant() {
      return this.$store.state.restaurant
        ? this.$store.state.restaurant
        : this.$store.state.company.restaurant;
    },
    showPrice() {
      const company = this.$store.state.company;
      if (!company.settings) return false;
      const settings = JSON.parse(company.settings);
      return !!settings?.show_price;
    },
    isDisabledWorker() {
      return (
        (this.dailyMenu.test !== this.meal.id + `_${this.shift.num}-smena` &&
          !!this.dailyMenu.test) ||
        (!this.restaurant.settings.rok_dana && this.meal.order?.quantity_payed > 0) ||
        (this.dayTo > this.dailyMenu.date && !this.params.checkTime)
      );
    },
    isDisabledCard() {
      return (
        this.disabled ||
        this.dailyMenu.disabled ||
        (this.dayTo > this.dailyMenu.date && !this.params.checkTime)
      );
    },
    companyBtnText() {
      return this.quantity > 0 ? this.$t("cancel") : this.$t("order");
    },
    isCompanyBtnDisabled() {
      return (
        (!this.restaurant.settings.rok_dana && this.meal.order?.quantity_payed > 0) ||
        (this.dayTo > this.dailyMenu.date && !this.params.checkTime)
      );
    },
  },
  methods: {
    openPreview() {
      this.previewDialog = true;
    },
    getLabelColor(label) {
      // Return label color if defined, otherwise use default colors
      if (label.color) return label.color;

      // Default colors based on label type or name
      const colorMap = {
        Veg: "green lighten-4",
        Vegetarian: "green lighten-4",
        Spicy: "red lighten-4",
        New: "blue lighten-4",
        Popular: "primary lighten-4",
        Healthy: "teal lighten-4",
      };

      return colorMap[label.name] || "grey lighten-3";
    },
    getLabelTextColor(label) {
      // Return text color based on label
      if (label.text_color) return label.text_color;

      const textColorMap = {
        Veg: "green darken-3",
        Vegetarian: "green darken-3",
        Spicy: "red darken-2",
        New: "blue darken-2",
        Popular: "primary darken-2",
        Healthy: "teal darken-3",
      };

      return textColorMap[label.name] || "grey darken-2";
    },
  },
};
</script>

<style scoped>
.food-card {
  margin: 16px 0;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.food-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  border-color: #d0d0d0;
}

.disabled-card {
  opacity: 0.6;
}

.food-card-disabled {
  position: absolute;
  background: #ffffff;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 10;
  opacity: 0.7;
  cursor: not-allowed;
}

/* Image Section */
.image-wrapper {
  position: relative;
}

.image-container {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  margin: 8px;
}

.image-container:hover .v-image {
  transform: scale(1.05);
}

.v-image {
  transition: transform 0.3s ease;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Content Section */
.content-section {
  flex: 1;
  min-width: 0;
}

.meal-title {
  font-weight: 700;
  line-height: 1.3;
  color: #1a1a1a;
  word-wrap: break-word;
}

/* Labels */
.labels-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Price Section */
.price-section {
  display: flex;
  align-items: center;
}

.price-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #ff6f00;
}

/* Info Chips */
.info-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Actions Section */
.actions-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 24px;
}

.quantity-btn {
  background: white !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.quantity-btn:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

.quantity-text {
  font-weight: 700;
  font-size: 1.2rem;
  min-width: 30px;
  text-align: center;
  color: #1a1a1a;
}

.action-btn {
  min-width: 80px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

/* Mobile styles */
.mobile-card {
  margin: 10px 0;
}

.mobile-card .image-container {
  margin: 6px;
}

.mobile-card .content-section {
  padding: 8px !important;
}

.mobile-card .meal-title {
  font-size: 0.95rem !important;
}

.mobile-card .price-text {
  font-size: 0.95rem;
}

.mobile-card .quantity-text {
  font-size: 1rem;
}

.mobile-card .quantity-controls {
  gap: 6px;
  padding: 2px 6px;
}

.mobile-card .action-btn {
  min-width: 70px;
  font-size: 0.85rem;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .food-card {
    margin: 8px 0;
  }

  .image-container {
    margin: 6px;
  }

  .content-section .pa-3 {
    padding: 10px !important;
  }
}
</style>
